// Primeiro import de propósito: define o locale global do dayjs/moment antes de
// qualquer tela ser avaliada (customRoutes/containers importam quase tudo).
import i18nProvider from './i18n';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useSyncExternalStore } from 'react';
import { Admin, CustomRoutes, Resource } from "react-admin";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Route } from 'react-router-dom';
import { Layout } from './containers';
import { FormErrorNotification } from './components/FormErrorNotification';
import customRoutes from './customRoutes';
import Home from './Home';
import { StoreProvider } from './hooks/useStore';
import { isEmpty } from './lib';
import authProvider from './providers/authProvider';
import { getAppLocale, subscribeAppLocale } from './i18n/locale';
import resources from './resources';
import ComponentWithPermissions from './resources/ComponentWithPermissions';
import FillinShow from './resources/fillins/FillinShow';
import login from './resources/login';
import { memoryStore } from './store/memoryStore';
import legacyDataProvider from './store/providers/react-admin-data';
import { theme } from './theme';
import { withAnalytics, identifyUser, usePageTracking } from './lib/analytics';
import { withRequestGuard } from './lib/requestGuard';
import { USER_KEY } from './constants';

// O guard fica por dentro do analytics: a requisição barrada aparece como erro em vez de sumir.
const dataProvider = withAnalytics(withRequestGuard(legacyDataProvider));

if (typeof window !== 'undefined' && localStorage.getItem(USER_KEY)) {
  try {
    const user = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
    if (user?.id) { void identifyUser(user); }
  } catch {
    /* ignore corrupt user blob */
  }
}

dayjs.extend(utc);
dayjs.extend(timezone);

// Detecta o timezone do navegador automaticamente
const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
dayjs.tz.setDefault(browserTimezone);

// Fora do componente: o App re-renderiza quando o idioma muda, e um memoryStore()
// novo a cada render zeraria o Store do react-admin.
const store = memoryStore();

const App = () => {
  const locale = useSyncExternalStore(subscribeAppLocale, getAppLocale);

  return (
    <StoreProvider>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={locale}
        dateLibInstance={dayjs}
      >
        <Admin
        dashboard={Home}
        loginPage={login}
        theme={theme}
        darkTheme={null}
        title="PontuaX"
        layout={Layout}
        notification={FormErrorNotification}
        dataProvider={dataProvider}
        authProvider={authProvider}
        i18nProvider={i18nProvider}
        store={store}
      >
        {
          permissions => {
            let initialResources = [...resources
              .filter(r => isEmpty(r.permissions) || r.permissions.some(r => permissions && permissions.includes(r)))
              .map(parameters => {
                Object.keys(parameters).forEach(key => {
                  const p = parameters[key];
                  if (p instanceof ComponentWithPermissions) {
                    parameters[key] = p.getComponent(permissions);
                  }
                });
                return <Resource {...parameters} key={parameters.name} hasCreate={!!parameters.create} hasEdit={!!parameters.edit} hasShow={!!parameters.show} />;
              })
            ];

            if (permissions) {
              initialResources = initialResources.concat(
                [
                  <Resource key="app-payment-methods" name="app-payment-methods" />,
                  <Resource key="app-art-files" name="app-art-files" />,
                  <Resource key="attendants" name="attendants" hasCreate hasEdit hasShow />,
                  <Resource key="billings" name="billings" hasCreate />,
                  <Resource key="chain-stations" name="chain-stations" />,
                  <Resource key="chains" name="chains" hasCreate hasEdit hasShow />,
                  <Resource key="chains-config" name="chains-config" />,
                  <Resource key="chains/chainId/customers/customerId/coupons" name="chains/chainId/customers/customerId/coupons" />,
                  <Resource key="chains/chainId/customers/transactions" name="chains/chainId/customers/transactions" />,
                  <Resource key="chains/chainId/devices" name="chains/chainId/devices" />,
                  <Resource key="chains/chainId/fillin-cashback" name="chains/chainId/fillin-cashback" />,
                  <Resource key="chains/chainId/import-points" name="chains/chainId/import-points" />,
                  <Resource key="chains/chainId/inbox-notifications" name="chains/chainId/inbox-notifications" />,
                  <Resource key="chains/chainId/limit-number-fillin" name="chains/chainId/limit-number-fillin" hasCreate />,
                  <Resource key="chains/chainId/marketing-campaigns" name="chains/chainId/marketing-campaigns" />,
                  <Resource key="chains/chainId/notification-messages" name="chains/chainId/notification-messages" />,
                  <Resource key="chains/chainId/point-fillin-interval" name="chains/chainId/point-fillin-interval" hasCreate />,
                  <Resource key="chains/chainId/point-limit" name="chains/chainId/point-limit" hasCreate />,
                  <Resource key="chains/chainId/reports/added-points" name="chains/chainId/reports/added-points" />,
                  <Resource key="chains/chainId/reports/advertisement-viwing" name="chains/chainId/reports/advertisement-viwing" />,
                  <Resource key="dashboard-v2/penetration-details" name="dashboard-v2/penetration-details" />,
                  <Resource key="dashboard-v2/push-details" name="dashboard-v2/push-details" />,
                  <Resource key="chains/chainId/reports/approved-transactions" name="chains/chainId/reports/approved-transactions" />,
                  <Resource key="chains/chainId/reports/attendant" name="chains/chainId/reports/attendant" />,
                  <Resource key="chains/chainId/reports/billings" name="chains/chainId/reports/billings" />,
                  <Resource key="chains/chainId/reports/blocked-customers" name="chains/chainId/reports/blocked-customers" />,
                  <Resource key="chains/chainId/reports/coupons" name="chains/chainId/reports/coupons" />,
                  <Resource key="chains/chainId/reports/customers" name="chains/chainId/reports/customers" />,
                  <Resource key="chains/chainId/reports/customers-deleted" name="chains/chainId/reports/customers-deleted" />,
                  <Resource key="chains/chainId/reports/customers-without-drawing" name="chains/chainId/reports/customers-without-drawing" />,
                  <Resource key="chains/chainId/reports/default-points" name="chains/chainId/reports/default-points" />,
                  <Resource key="chains/chainId/reports/detailed-points" name="chains/chainId/reports/detailed-points" />,
                  <Resource key="chains/chainId/reports/device-customer" name="chains/chainId/reports/device-customer" />,
                  <Resource key="chains/chainId/reports/discount" name="chains/chainId/reports/discount" />,
                  <Resource key="chains/chainId/reports/dotz-refuel-transactions" name="chains/chainId/reports/dotz-refuel-transactions" />,
                  <Resource key="chains/chainId/reports/drawing" name="chains/chainId/reports/drawing" />,
                  <Resource key="chains/chainId/reports/favorite-station" name="chains/chainId/reports/favorite-station" />,
                  <Resource key="chains/chainId/reports/fillin-by-attendant" name="chains/chainId/reports/fillin-by-attendant" />,
                  <Resource key="chains/chainId/reports/fillin-by-customer" name="chains/chainId/reports/fillin-by-customer" />,
                  <Resource key="chains/chainId/reports/fillin-by-fuel" name="chains/chainId/reports/fillin-by-fuel" />,
                  <Resource key="chains/chainId/reports/fillin-by-station" name="chains/chainId/reports/fillin-by-station" />,
                  <Resource key="chains/chainId/reports/fillins" name="chains/chainId/reports/fillins" />,
                  <Resource key="chains/chainId/reports/gifts" name="chains/chainId/reports/gifts" />,
                  <Resource key="chains/chainId/reports/first-fillin-by-attendant" name="chains/chainId/reports/first-fillin-by-attendant" />,
                  <Resource key="chains/chainId/reports/first-fillin-by-station" name="chains/chainId/reports/first-fillin-by-station" />,
                  <Resource key="chains/chainId/reports/fixed-discount-report" name="chains/chainId/reports/fixed-discount-report" />,
                  <Resource key="chains/chainId/reports/inactive-customers" name="chains/chainId/reports/inactive-customers" />,
                  <Resource key="chains/chainId/reports/monthly-evolution" name="chains/chainId/reports/monthly-evolution" />,
                  <Resource key="chains/chainId/reports/new-customers" name="chains/chainId/reports/new-customers" />,
                  <Resource key="chains/chainId/reports/notifications" name="chains/chainId/reports/notifications" />,
                  <Resource key="chains/chainId/reports/partner-values" name="chains/chainId/reports/partner-values" />,
                  <Resource key="chains/chainId/reports/partners" name="chains/chainId/reports/partners" />,
                  <Resource key="chains/chainId/reports/payments-by-pix" name="chains/chainId/reports/payments-by-pix" />,
                  <Resource key="chains/chainId/reports/pending-transaction" name="chains/chainId/reports/pending-transaction" />,
                  <Resource key="chains/chainId/reports/points-balance" name="chains/chainId/reports/points-balance" />,
                  <Resource key="chains/chainId/reports/products" name="chains/chainId/reports/products" />,
                  <Resource key="chains/chainId/reports/recurring-customers" name="chains/chainId/reports/recurring-customers" />,
                  <Resource key="chains/chainId/reports/redeemed-coupons" name="chains/chainId/reports/redeemed-coupons" />,
                  <Resource key="chains/chainId/reports/redemption-x-points" name="chains/chainId/reports/redemption-x-points" />,
                  <Resource key="chains/chainId/reports/refered-customers" name="chains/chainId/reports/refered-customers" />,
                  <Resource key="chains/chainId/reports/refunded-coupons" name="chains/chainId/reports/refunded-coupons" />,
                  <Resource key="chains/chainId/reports/registered-discounts" name="chains/chainId/reports/registered-discounts" />,
                  <Resource key="chains/chainId/reports/registration-customers" name="chains/chainId/reports/registration-customers" />,
                  <Resource key="chains/chainId/reports/roleta" name="chains/chainId/reports/roleta" />,
                  <Resource key="chains/chainId/reports/station" name="chains/chainId/reports/station" />,
                  <Resource key="chains/chainId/reports/top-customers" name="chains/chainId/reports/top-customers" />,
                  <Resource key="chains/chainId/station-groups" name="chains/chainId/station-groups" hasCreate hasEdit />,
                  <Resource key="chains/chainId/stations" name="chains/chainId/stations" />,
                  <Resource key="client-categories" name="client-categories" />,
                  <Resource key="companies" name="companies" />,
                  <Resource key="coupons" name="coupons" />,
                  <Resource key="customer-balance" name="customer-balance" />,
                  <Resource key="customer-discounts" name="customer-discounts" />,
                  <Resource key="customer-scorings" name="customer-scorings" />,
                  <Resource key="customer-last-fillin" name="customer-last-fillin" />,
                  <Resource key="customers" name="customers" hasShow hasEdit />,
                  <Resource key="customers/customerId/transactions" name="customers/customerId/transactions" options={{ customerId: null }} />,
                  <Resource key="drawing-lucky-numbers" name="drawing-lucky-numbers" />,
                  <Resource key="drawing-rule-fuels" name="drawing-rule-fuels" />,
                  <Resource key="drawing-rules" name="drawing-rules" />,
                  <Resource key="employees" name="employees" hasCreate hasEdit hasShow />,
                  <Resource key="employees/recovery" name="employees/recovery" />,
                  <Resource key="fillins" name="fillins" show={FillinShow} />,
                  <Resource key="fuel-discounts" name="fuel-discounts" />,
                  <Resource key="fuels" name="fuels" />,
                  <Resource key="permissions" name="permissions" />,
                  <Resource key="places" name="places" hasCreate hasEdit hasShow />,
                  <Resource key="rating-poll-items" name="rating-poll-items" />,
                  <Resource key="report-coupons" name="report-coupons" />,
                  <Resource key="reports/concentrator-error" name="reports/concentrator-error" />,
                  <Resource key="reports/current-fillins" name="reports/current-fillins" />,
                  <Resource key="reports/expiring-points-by-customer" name="reports/expiring-points-by-customer" />,
                  <Resource key="reports/expiring-points-by-date" name="reports/expiring-points-by-date" />,
                  <Resource key="reports/external-partners" name="reports/external-partners" />,
                  <Resource key="reports/price-divergence" name="reports/price-divergence" />,
                  <Resource key="reports/socket-error" name="reports/socket-error" />,
                  <Resource key="reports/station-rating" name="reports/station-rating" />,
                  <Resource key="sales" name="sales" />,
                  <Resource key="security-rules" name="security-rules" />,
                  <Resource key="station-attendants" name="station-attendants" />,
                  <Resource key="station-qrcodes" name="station-qrcodes" />,
                  <Resource key="transaction-redemptions" name="transaction-redemptions" />,
                  <Resource key="transactions" name="transactions" />,
                  <Resource key="vehicles" name="vehicles" />,
                  <Resource key="partner-payments" name="partner-payments" hasShow />,
                ]
              );
            }
            return initialResources;
          }}
        <CustomRoutes>
          {
            customRoutes.map(customRoute => <Route key={customRoute.path} path={customRoute.path} element={customRoute.element} />)
          }
        </CustomRoutes>
        </Admin>
      </LocalizationProvider>
    </StoreProvider>
  );
};

export default App;
